import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, ArrowRight, XCircle } from "@phosphor-icons/react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          setStatus("failed"); return;
        }
        if (attempts < 15) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch { setStatus("error"); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [attempts, sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <div className="max-w-lg w-full border border-[#262626] bg-[#121212] p-10 text-center" data-testid="payment-result">
        {status === "checking" && (
          <>
            <div className="animate-pulse text-[#FF3B30] font-heading uppercase tracking-widest text-sm">Confirming payment…</div>
            <div className="mt-6 text-[#A3A3A3] text-sm">Attempt {attempts + 1}/16</div>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={64} weight="fill" className="text-[#34C759] mx-auto mb-4" />
            <h1 className="font-heading font-black uppercase text-4xl">You're In</h1>
            <p className="text-[#A3A3A3] mt-3">Subscription active. Full access unlocked.</p>
            <button onClick={() => nav("/dashboard")} data-testid="go-dashboard"
              className="mt-8 inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63026] text-white px-6 py-3 font-heading uppercase tracking-widest text-sm font-bold">
              Go To Dashboard <ArrowRight size={16} weight="bold" />
            </button>
          </>
        )}
        {(status === "failed" || status === "error" || status === "timeout") && (
          <>
            <XCircle size={64} weight="fill" className="text-[#FF3B30] mx-auto mb-4" />
            <h1 className="font-heading font-black uppercase text-3xl">Payment Not Confirmed</h1>
            <p className="text-[#A3A3A3] mt-3">Try again from the pricing page.</p>
            <Link to="/pricing" className="mt-8 inline-block border border-[#FF3B30] text-[#FF3B30] px-6 py-3 font-heading uppercase tracking-widest text-sm">Back to Pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
